/**
 * Sawyer E4 (#754) — "New message": pick a co-worker. Active staff at this
 * facility only, yourself excluded, each with their role (the server's
 * GET /people decides who is listed).
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { personName, roleLabel, staffMessagesApi, type Person } from '../../lib/staff-messages-api.js';

type State = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; people: Person[] };

export function PeoplePicker({
  apiUrl,
  onPick,
  onClose,
}: {
  apiUrl: string;
  onPick: (p: Person) => void;
  onClose: () => void;
}): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    let cancelled = false;
    void staffMessagesApi.people(apiUrl).then((r) => {
      if (!cancelled) setState(r.ok ? { kind: 'ready', people: r.data.people } : { kind: 'error' });
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div className="ferro-tm-overlay" onKeyDown={onKeyDown}>
      <div className="ferro-tm-overlay__backdrop" onClick={onClose} aria-hidden="true" />
      <div className="ferro-tm-dialog" role="dialog" aria-modal="true" aria-labelledby="tm-people-title" data-testid="people-picker">
        <div className="ferro-tm-dialog__head">
          <h2 id="tm-people-title" className="ferro-tm-dialog__title">
            New message
          </h2>
          <button ref={closeRef} type="button" className="ferro-tm-dialog__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {state.kind === 'loading' ? <p className="ferro-tm-dialog__note">Loading your team…</p> : null}
        {state.kind === 'error' ? (
          <p className="ferro-tm-dialog__note" role="alert">
            Couldn&apos;t load your team. Check your connection and try again.
          </p>
        ) : null}
        {state.kind === 'ready' && state.people.length === 0 ? (
          <p className="ferro-tm-dialog__note">No one else at this facility has an account yet.</p>
        ) : null}
        {state.kind === 'ready' && state.people.length > 0 ? (
          <ul className="ferro-tm-dialog__list" aria-label="Your team">
            {state.people.map((p) => (
              <li key={p.id}>
                <button type="button" className="ferro-tm-dialog__person" onClick={() => onPick(p)} data-testid="people-option">
                  <span className="ferro-tm-dialog__option-name">{personName(p)}</span>
                  {roleLabel(p.role) ? <span className="ferro-tm-dialog__option-meta">{roleLabel(p.role)}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
